import { createApiClient, createIdentityApi } from '@hb/api-client';
import { computed, shallowRef } from 'vue';
/**
 * The session every SPA and the Capacitor app share: it owns the access token,
 * exchanges the SSO cookie on load, and exposes the identity API already
 * authenticated.
 *
 * It builds the HTTP client rather than receiving one, because the client needs
 * `getAccessToken` and `onTokensRefreshed` - both of which are this object's
 * own state. Handing them in from outside would put the token in two places.
 */
export function createAuthSession(options) {
    const vault = options.nativeVault;
    const clientKind = vault ? 'native' : 'web';
    /**
     * NOT a `ref`, deliberately.
     *
     * Nothing needs to *react* to the access token - its only consumer is the
     * HTTP client, once per request. Making it reactive would put a bearer
     * credential into the Vue devtools tree and into any state snapshot a future
     * error reporter decides to take. A closure variable is unreachable from all
     * of that, and it dies with the tab, which is the point: this token is never
     * written to `localStorage` or anywhere else that survives a reload. The
     * cookie is what survives, and script cannot read it.
     */
    let accessToken = null;
    // shallowRef throughout: these hold immutable snapshots that are replaced
    // wholesale, so deep reactivity would walk every property for nothing.
    const user = shallowRef(null);
    const grants = shallowRef([]);
    const status = shallowRef('unknown');
    const unreachable = shallowRef(false);
    let bootstrapInFlight = null;
    /**
     * Forgets the session **in this page**. Never the vault's copy: that is
     * forgotten by the vault itself when the server refuses it, and by
     * `signOut` when the user asks - and by nothing else, so a failed bootstrap
     * on a phone with no signal leaves the session there for the next attempt.
     */
    function clear() {
        accessToken = null;
        user.value = null;
        grants.value = [];
    }
    function endSession() {
        clear();
        status.value = 'anonymous';
        options.onSignedOut?.();
    }
    // Only ever the access token. A web client's refresh token is a cookie, and a
    // native client's is the vault's - which hands back access tokens alone.
    function keep(tokens) {
        accessToken = tokens.accessToken;
    }
    const client = createApiClient({
        baseUrl: options.baseUrl,
        getAccessToken: () => accessToken,
        onTokensRefreshed: keep,
        // Reached when a 401 could not be repaired by a refresh: the refresh token
        // is gone, expired, or was revoked by reuse detection on another device.
        onAuthenticationLost: endSession,
        ...(options.fetch ? { fetch: options.fetch } : {}),
        ...(vault ? { refreshSession: () => vault.refresh() } : {}),
        ...(options.appVersion !== undefined ? { appVersion: options.appVersion } : {}),
        ...(options.onUpdateRequired ? { onUpdateRequired: options.onUpdateRequired } : {}),
    });
    const api = createIdentityApi(client);
    function adopt(started) {
        keep(started);
        user.value = started.user;
        grants.value = started.grants;
        status.value = 'authenticated';
    }
    async function loadAccount() {
        const me = await api.me();
        user.value = me.user;
        grants.value = me.grants;
    }
    async function runBootstrap() {
        // A failed refresh here must NOT go through `onAuthenticationLost`: having
        // no session on a cold load is the normal case, not a session being taken
        // away, and firing a "you were signed out" callback at someone who was
        // never signed in is how you get a spurious toast on the login screen.
        const outcome = await client.refresh();
        unreachable.value = outcome === 'unreachable';
        if (outcome !== 'refreshed') {
            clear();
            status.value = 'anonymous';
            return status.value;
        }
        try {
            await loadAccount();
            status.value = 'authenticated';
        }
        catch {
            // Any failure means we cannot prove there is a session, so we say there
            // is not. That includes a transient 502, which would send the user to the
            // sign-in screen - `bootstrap({ force: true })` is the way back from it.
            clear();
            status.value = 'anonymous';
        }
        return status.value;
    }
    async function bootstrap(bootstrapOptions = {}) {
        if (!bootstrapOptions.force && status.value !== 'unknown')
            return status.value;
        bootstrapInFlight ??= runBootstrap().finally(() => {
            bootstrapInFlight = null;
        });
        return bootstrapInFlight;
    }
    async function completeLogin(started) {
        const session = await started;
        // A native sign-in is answered with the refresh token in the body, and this
        // is the one moment script holds it. It goes to the vault before the session
        // is declared open: a session the next launch could not resume is not one
        // worth reporting as started, and a vault that refuses it is a failure the
        // sign-in screen has to show.
        if (vault && session.refreshToken !== undefined)
            await vault.keep(session.refreshToken);
        adopt(session);
        return session;
    }
    return {
        user,
        grants,
        status,
        unreachable,
        isAuthenticated: computed(() => status.value === 'authenticated'),
        isAdmin: computed(() => user.value?.isAdmin === true),
        can: (app) => grants.value.includes(app),
        api,
        client,
        bootstrap,
        // The client kind is fixed for the life of the app, so it is filled in here
        // rather than asked of every screen. It has to match what the refresh token
        // is stored as, and a screen is the wrong place to get that right.
        login: (body) => api.login({ ...body, clientKind }),
        completeLoginWithTotp: (body) => completeLogin(api.completeLoginWithTotp(body)),
        completeLoginWithRecoveryCode: (body) => completeLogin(api.completeLoginWithRecoveryCode(body)),
        adopt,
        async signOut(signOutOptions = {}) {
            try {
                await api.logout(signOutOptions.allDevices === true ? { allDevices: true } : {});
            }
            finally {
                // In `finally`, but deliberately NOT swallowing the error.
                //
                // The local session goes either way - leaving someone signed in because
                // the network blinked is the worse failure. But the rejection still has
                // to reach the caller: if the request never landed, the server-side
                // session is alive and so is the cookie, so the next load would quietly
                // sign this person back in. Only the UI can offer them a retry.
                //
                // The vault is emptied here too, and inside its own `finally`: a vault
                // that fails to forget must still leave the page signed out, because
                // the one thing a sign-out must never do is fail in a way that leaves
                // the session usable.
                try {
                    await vault?.forget();
                }
                finally {
                    endSession();
                }
            }
        },
        async deleteAccount(body) {
            const scheduled = await api.requestAccountDeletion(body);
            // Swallowed, unlike in `signOut`. The server has already revoked the token
            // the vault holds, so a vault that fails to forget it keeps something
            // useless - its next refresh is refused and it forgets then. Throwing here
            // would tell somebody whose deletion has been scheduled that it failed.
            await vault?.forget().catch(() => undefined);
            clear();
            status.value = 'anonymous';
            return scheduled;
        },
        async reload() {
            if (status.value !== 'authenticated')
                return;
            await loadAccount();
        },
    };
}
//# sourceMappingURL=session.js.map